// src/components/chart/DynamicChart.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import zoomPlugin from 'chartjs-plugin-zoom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  zoomPlugin
);

type Serie = {
  name: string;
  data: number[];
};

type ApiResponse = {
  date_ref: string;
  years: number[];
  months: number[];
  series: Serie[];
};

type Props = {
  clientCode: string;
  payload: {
    typefiche: 'CONTRAT' | 'PASSAGE';
    idtypcont?: number | null;
    subject: string;
    dateDeb: string;
    dateFin: string;
  };
  height?: number;
  className?: string;
};



// const COLORS = [
//   '#4f46e5',
//   '#06b6d4',
//   '#22c55e',
//   '#f59e0b',
//   '#ef4444',
//   '#a855f7',
//   '#14b8a6',
//   '#3b82f6',
// ];

function generateColor(index: number, total: number) {
  const hue = Math.round((360 / total) * index);
  return {
    border: `hsl(${hue}, 70%, 50%)`,
    background: `hsla(${hue}, 70%, 50%, 0.2)`,
  };
}


export default function DynamicChart({
  clientCode,
  payload,
  height = 320,
  className = '',
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<ApiResponse | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await api.post<ApiResponse>(
          '/dynamic-chart',
          payload,
          { params: { clientCode } }
        );

        if (!cancelled) setData(res.data);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.error || 'Erreur chargement graphique');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientCode, payload]);

  const SUBJECTS = [
    { key: 'libtypcont', label: 'Type de fiche' },
    { key: 'quantite', label: 'Total contrat' },
    { key: 'isopavillon', label: 'Pavillon' },
    { key: 'isopays', label: 'Pays' },
    { key: 'codpost', label: 'Département' },
    { key: 'libcatbat', label: 'Catégorie bateau' },
    { key: 'libtypbat', label: 'Type bateau' },
    { key: 'libtypcli', label: 'Type client' },
  ];


  const subjectLabel = useMemo(() => {
    return (
      SUBJECTS.find(s => s.key === payload.subject)?.label
      ?? payload.subject
    );
  }, [payload.subject]);


  const chartData: ChartData<'line'> | null = useMemo(() => {
    if (!data) return null;

    const labels = data.years.map(String);
    const totalSeries = data.series.length;

    const datasets = data.series.map((serie, idx) => {
      const color = generateColor(idx, totalSeries);

      return {
        label: serie.name,
        data: serie.data,
        borderColor: color.border,
        backgroundColor: color.background,
        borderWidth: 2,
        pointRadius: 4,
        tension: 0.3,
      };
    });

    return { labels, datasets };
  }, [data]);



  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      zoom: {
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        grid: { color: 'rgba(0,0,0,0.08)' },
      },
    },
  };

  return (
    <div
      className={`rounded-2xl border border-gray-200 bg-white px-5 pb-4 pt-5 dark:border-darkBorderCard dark:bg-white/[0.03] sm:px-6 sm:pt-6 ${className}`}
    >
      <h3 className="mb-3 text-lg font-semibold text-gray-800 dark:text-white/90">
        Analyse — {subjectLabel}
    </h3>


      {loading && <div className="text-gray-500">Chargement…</div>}
      {error && !loading && <div className="text-red-600">{error}</div>}

      {!loading && !error && chartData && (
        <div style={{ height }}>
          <Line data={chartData} options={options} />
        </div>
      )}
    </div>
  );
}
